import { AionCta } from "@/components/site/AionCta";
import { KeywordHubLinks } from "@/components/site/KeywordHubLinks";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, BookOpen, Download } from "lucide-react";
import { PageBreadcrumb } from "@/components/site/PageBreadcrumb";

export const Route = createFileRoute("/build-dont-talk")({
  head: () => ({
    meta: [
      { title: "Raj Shamani Books — Build Don't Talk: Summary & Where to Buy", key: "title" },
      { name: "description", content: "Raj Shamani's book Build Don't Talk — summary, key lessons and where to buy on Amazon and Flipkart. India's bestselling money and mindset guide.", key: "description" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1", key: "robots" },
      { property: "og:title", content: "Raj Shamani Books — Build Don't Talk: Summary & Where to Buy", key: "og:title" },
      { property: "og:description", content: "Raj Shamani's book Build Don't Talk — summary, key lessons and where to buy on Amazon and Flipkart. India's bestselling money and mindset guide.", key: "og:description" },
      { property: "og:url", content: "https://rajshamani.life/build-dont-talk", key: "og:url" },
      { property: "og:type", content: "book", key: "og:type" },
      { property: "og:site_name", content: "Raj Shamani", key: "og:site_name" },
      { property: "og:locale", content: "en_IN", key: "og:locale" },
      { property: "book:author", content: "Raj Shamani", key: "book:author" },
      { property: "book:isbn", content: "9789356291317", key: "book:isbn" },
      { property: "book:release_date", content: "2022-09-01", key: "book:release_date" },
      { property: "book:tag", content: "self-help", key: "book:tag" },
      { property: "book:tag", content: "personal finance", key: "book:tag2" },
      { property: "book:tag", content: "entrepreneurship", key: "book:tag3" },
      { property: "og:image", content: "https://rajshamani.life/__l5e/assets-v1/adbb3be6-adb3-4452-bcdb-2ec61d2c8743/og-book.jpg", key: "og:image" },
      { property: "og:image:alt", content: "Build Don't Talk by Raj Shamani — bestselling book cover", key: "og:image:alt" },
      { name: "twitter:card", content: "summary_large_image", key: "twitter:card" },
      { name: "twitter:site", content: "@rajshamani", key: "twitter:site" },
      { name: "twitter:creator", content: "@rajshamani", key: "twitter:creator" },
      { name: "twitter:title", content: "Raj Shamani Books — Build Don't Talk: Summary & Where to Buy", key: "twitter:title" },
      { name: "twitter:description", content: "Raj Shamani's book Build Don't Talk — summary, key lessons and where to buy on Amazon and Flipkart.", key: "twitter:description" },
      { name: "twitter:image", content: "https://rajshamani.life/__l5e/assets-v1/adbb3be6-adb3-4452-bcdb-2ec61d2c8743/og-book.jpg", key: "twitter:image" },
    ],
    links: [{ rel: "canonical", href: "https://rajshamani.life/build-dont-talk" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Book",
          name: "Build Don't Talk",
          author: { "@type": "Person", name: "Raj Shamani", url: "https://rajshamani.life/" },
          inLanguage: "en",
          bookFormat: "https://schema.org/Paperback",
          isbn: "9789356291317",
          publisher: { "@type": "Organization", name: "Invincible Publishers" },
          datePublished: "2022-09-01",
          description:
            "Build Don't Talk is Raj Shamani's bestselling guide on how to think, earn and live in the modern Indian economy — covering money, mindset, communication, and building leverage.",
          url: "https://rajshamani.life/build-dont-talk",
          offers: [
            {
              "@type": "Offer",
              name: "Amazon India",
              url: "https://www.amazon.in/Build-Dont-Talk-Things-Taught/dp/0143459686",
              availability: "https://schema.org/InStock",
              priceCurrency: "INR",
              seller: { "@type": "Organization", name: "Amazon" },
            },
            {
              "@type": "Offer",
              name: "Flipkart",
              url: "https://www.flipkart.com/build-don-t-talk/p/itm81394118c089f",
              availability: "https://schema.org/InStock",
              priceCurrency: "INR",
              seller: { "@type": "Organization", name: "Flipkart" },
            },
          ],
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "4.5",
            reviewCount: "1200",
            bestRating: "5",
            worstRating: "1",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Raj Shamani Books — Build Don't Talk: Summary & Where to Buy",
          headline: "Raj Shamani Books — Build Don't Talk: Summary & Where to Buy",
          description: "Raj Shamani's book Build Don't Talk — summary, key lessons and where to buy on Amazon and Flipkart.",
          url: "https://rajshamani.life/build-dont-talk",
          inLanguage: "en",
          isPartOf: { "@type": "WebSite", name: "Raj Shamani", url: "https://rajshamani.life/" },
          about: { "@type": "Book", name: "Build Don't Talk", isbn: "9789356291317" },
          primaryImageOfPage: {
            "@type": "ImageObject",
            url: "https://rajshamani.life/__l5e/assets-v1/adbb3be6-adb3-4452-bcdb-2ec61d2c8743/og-book.jpg",
          },
          image: "https://rajshamani.life/__l5e/assets-v1/adbb3be6-adb3-4452-bcdb-2ec61d2c8743/og-book.jpg",
        }),
      },
    ],
  }),
  component: BuildDontTalk,
});

const takeaways = [
  {
    t: "Money is a skill, not a secret",
    d: "Most people aren't bad with money — they were never taught the rules. The book breaks down earning, saving and investing in plain Indian context.",
  },
  {
    t: "Communication is your unfair advantage",
    d: "How you speak, write and present compounds faster than almost any other skill. Build it early, build it deliberately.",
  },
  {
    t: "Build leverage, not just income",
    d: "Trading hours for rupees has a ceiling. Content, code, capital and brand are the four kinds of leverage that scale beyond you.",
  },
  {
    t: "Stop talking, start shipping",
    d: "The world rewards what you build, not what you announce. Output is the only resume that matters.",
  },
  {
    t: "Your network is your net worth",
    d: "Who you spend time with sets the ceiling of what you think is possible. Curate ruthlessly.",
  },
];

function BuildDontTalk() {
  return (
    <div>
      <PageBreadcrumb current="Build Don't Talk" />
      <section className="bg-noir border-b border-border/50">
        <div className="mx-auto max-w-5xl px-6 py-24">
          <div className="text-xs tracking-widest uppercase text-gold mb-4 inline-flex items-center gap-2">
            <BookOpen size={14} /> The Book
          </div>
          <h1 className="text-5xl md:text-6xl font-bold leading-tight">
            Build Don't Talk <span className="text-gradient-gold">— by Raj Shamani</span>
          </h1>
          <p className="mt-8 text-lg text-muted-foreground max-w-3xl leading-relaxed">
            <em>Build Don't Talk</em> is Raj Shamani's bestselling book on how
            to think, earn and live in the modern Indian economy. It distills
            a decade of lessons from running a family business, building a
            personal brand, investing in startups and hosting India's biggest
            founders on Figuring Out — into a playbook anyone in their teens,
            twenties or thirties can actually use.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <a
              href="https://www.amazon.in/Build-Dont-Talk-Things-Taught/dp/0143459686"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 px-6 py-3 rounded-sm bg-gold text-primary-foreground font-medium hover:opacity-90 transition"
            >
              Buy on Amazon
              <ArrowRight size={16} className="group-hover:translate-x-1 transition" />
            </a>
            <a
              href="https://www.flipkart.com/build-don-t-talk/p/itm81394118c089f"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-sm border border-border text-foreground hover:border-gold/60 hover:text-gold transition"
            >
              Buy on Flipkart
            </a>
            <a
              href="/build-dont-talk-summary.pdf"
              download
              className="inline-flex items-center gap-2 px-6 py-3 rounded-sm border border-gold/60 text-gold hover:bg-gold hover:text-primary-foreground transition"
            >
              <Download size={16} /> Download Summary (PDF)
            </a>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-24">
        <h2 className="text-3xl md:text-4xl font-bold mb-12">What you'll learn</h2>
        <div className="space-y-10">
          {takeaways.map((k, i) => (
            <div
              key={i}
              className="grid md:grid-cols-[80px_1fr] gap-6 pb-10 border-b border-border/50 last:border-0"
            >
              <div className="text-gold font-display font-bold text-2xl">
                0{i + 1}
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">{k.t}</h3>
                <p className="text-muted-foreground leading-relaxed">{k.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-24">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="p-8 rounded-xl bg-card border border-border">
            <h3 className="text-2xl font-bold mb-3 text-gradient-gold">Who it's for</h3>
            <p className="text-muted-foreground">
              Students, early-career professionals, first-time founders, and
              anyone who is tired of motivational fluff and wants a direct,
              India-first take on money, work and building a life.
            </p>
          </div>
          <div className="p-8 rounded-xl bg-card border border-border">
            <h3 className="text-2xl font-bold mb-3 text-gradient-gold">The promise</h3>
            <p className="text-muted-foreground">
              Less theory, more execution. By the last page you should have a
              clearer view of what to start, what to stop, and what to double
              down on for the next ten years.
            </p>
          </div>
        </div>

        <div className="mt-16 text-center">
          <Link
            to="/raj-shamani-books"
            className="inline-flex items-center gap-2 text-gold hover:underline"
          >
            See all Raj Shamani books <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <AionCta />
      <KeywordHubLinks />

    </div>
  );
}
